import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Clock, Car, MapPin } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './DrivingTimeCalculatorPage/components/CalculationForm';
import ResultsOverview from './DrivingTimeCalculatorPage/components/ResultsOverview';
import RouteDetails from './DrivingTimeCalculatorPage/components/RouteDetails';
import QuickAdjustmentSidebar from './DrivingTimeCalculatorPage/components/QuickAdjustmentSidebar';
import SavedRoutesSidebar from './DrivingTimeCalculatorPage/components/SavedRoutesSidebar';
import TipsSidebar from './DrivingTimeCalculatorPage/components/TipsSidebar';
import InfoContent from './DrivingTimeCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  startPoint: '',
  endPoint: '',
  distance: '100', // Километры
  averageSpeed: '90', // Км/ч
  vehicleType: 'car', // Тип транспорта
  breaks: true, // Учитывать перерывы
  breakInterval: '2', // Часы вождения перед перерывом
  breakDuration: '15', // Минуты перерыва
  roadType: 'mixed', // Тип дороги
  trafficJams: 'moderate', // Пробки
  weatherCondition: 'clear', // Погодные условия
  departureTime: '08:00', // Время выезда
  departureDate: new Date().toISOString().split('T')[0], // Дата выезда
  numPassengers: '1', // Количество пассажиров
  fuelConsumption: '8', // Л/100км
  fuelPrice: '50', // Рублей за литр
  tollRoads: false, // Учитывать платные дороги
  tollRoadsCost: '0', // Стоимость платных дорог
  includeStops: false, // Запланированные остановки
  stops: [], // Список остановок {name, duration} (в минутах)
};

// Типы транспорта
export const vehicleTypes = [
  { id: 'car', name: 'Легковой автомобиль', defaultSpeed: 90 },
  { id: 'motorcycle', name: 'Мотоцикл', defaultSpeed: 80 },
  { id: 'bus', name: 'Автобус', defaultSpeed: 70 },
  { id: 'truck', name: 'Грузовик', defaultSpeed: 60 },
  { id: 'minivan', name: 'Минивэн', defaultSpeed: 85 },
  { id: 'suv', name: 'Внедорожник', defaultSpeed: 80 }
];

// Типы дорог
export const roadTypes = [
  { id: 'highway', name: 'Автомагистраль', speedFactor: 1.2 },
  { id: 'federal', name: 'Федеральная трасса', speedFactor: 1.0 },
  { id: 'regional', name: 'Региональная дорога', speedFactor: 0.9 },
  { id: 'urban', name: 'Городская дорога', speedFactor: 0.7 },
  { id: 'rural', name: 'Сельская дорога', speedFactor: 0.8 },
  { id: 'mixed', name: 'Смешанный тип', speedFactor: 1.0 }
];

// Уровни пробок
export const trafficLevels = [
  { id: 'none', name: 'Отсутствуют', timeFactor: 1.0 },
  { id: 'light', name: 'Лёгкие', timeFactor: 1.1 },
  { id: 'moderate', name: 'Умеренные', timeFactor: 1.25 },
  { id: 'heavy', name: 'Сильные', timeFactor: 1.5 },
  { id: 'severe', name: 'Очень сильные', timeFactor: 2.0 }
];

// Погодные условия
export const weatherConditions = [
  { id: 'clear', name: 'Ясно', timeFactor: 1.0 },
  { id: 'rain', name: 'Дождь', timeFactor: 1.1 },
  { id: 'snow', name: 'Снег', timeFactor: 1.3 },
  { id: 'fog', name: 'Туман', timeFactor: 1.2 },
  { id: 'ice', name: 'Гололед', timeFactor: 1.4 },
  { id: 'storm', name: 'Шторм/Ураган', timeFactor: 1.5 }
];

/**
 * DrivingTimeCalculatorPage Component - Калькулятор времени в пути на автомобиле
 */
const DrivingTimeCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('driving-time-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор времени в пути на автомобиле | Gensio';
  const metaDescription = tool?.metaDescription || 'Рассчитайте точное время в пути на автомобиле с учетом расстояния, типа дороги, пробок, погодных условий и запланированных остановок. Калькулятор учитывает специфику российских дорог и дает прогноз расхода топлива и стоимости поездки.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [routeDetails, setRouteDetails] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedRoutes, setSavedRoutes] = useState([]);
  const [showRouteSummary, setShowRouteSummary] = useState(false);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных маршрутов из localStorage ---
  useEffect(() => {
    const savedRoutesData = localStorage.getItem('savedRoutes');
    if (savedRoutesData) {
      try {
        setSavedRoutes(JSON.parse(savedRoutesData));
      } catch (e) {
        console.error("Error loading saved routes:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      const findVehicle = (id) => {
        const found = vehicleTypes.find(t => t.id === id);
        return found ? found.defaultSpeed.toString() : initialFormData.averageSpeed;
      };

      switch (subToolId) {
        case 'car':
          newState.vehicleType = 'car';
          newState.averageSpeed = findVehicle('car');
          break;
        case 'motorcycle':
          newState.vehicleType = 'motorcycle';
          newState.averageSpeed = findVehicle('motorcycle');
          break;
        case 'truck':
          newState.vehicleType = 'truck';
          newState.averageSpeed = findVehicle('truck');
          break;
        case 'highway':
          newState.roadType = 'highway';
          break;
        case 'urban':
          newState.roadType = 'urban';
          break;
        case 'with-stops':
          newState.includeStops = true;
          break;
        case 'toll-roads':
          newState.tollRoads = true;
          newState.tollRoadsCost = '500';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setRouteDetails(null);
    setErrors({});
    setShowRouteSummary(false);

  }, [subToolId]);

  // Сохранение маршрута в localStorage
  const saveRoute = (name) => {
    if (!results) return;

    const newSavedRoute = {
      id: Date.now().toString(),
      name: name || `${formData.startPoint || 'Пункт А'} - ${formData.endPoint || 'Пункт Б'}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      routeDetails: routeDetails
    };

    const updatedSavedRoutes = [...savedRoutes, newSavedRoute];
    setSavedRoutes(updatedSavedRoutes);
    localStorage.setItem('savedRoutes', JSON.stringify(updatedSavedRoutes));

    return newSavedRoute;
  };

  // Удаление сохраненного маршрута
  const deleteSavedRoute = (id) => {
    const updatedSavedRoutes = savedRoutes.filter(route => route.id !== id);
    setSavedRoutes(updatedSavedRoutes);
    localStorage.setItem('savedRoutes', JSON.stringify(updatedSavedRoutes));
  };

  // Загрузка сохраненного маршрута
  const loadSavedRoute = (id) => {
    const routeToLoad = savedRoutes.find(route => route.id === id);
    if (routeToLoad) {
      setFormData(routeToLoad.formData);
      setResults(routeToLoad.results);
      setRouteDetails(routeToLoad.routeDetails);
      setShowRouteSummary(true);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Car className="w-8 h-8 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор времени в пути на автомобиле'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              routeDetails={routeDetails}
              setRouteDetails={setRouteDetails}
              vehicleTypes={vehicleTypes}
              roadTypes={roadTypes}
              trafficLevels={trafficLevels}
              weatherConditions={weatherConditions}
              saveRoute={saveRoute}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <RouteDetails 
                  routeDetails={routeDetails} 
                  results={results}
                  showRouteSummary={showRouteSummary}
                  setShowRouteSummary={setShowRouteSummary}
                />
              </>
            )}

            {/* --- Информационный контент о поездках на авто --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              vehicleTypes={vehicleTypes}
            />

            {/* --- Панель сохраненных маршрутов --- */}
            <SavedRoutesSidebar 
              savedRoutes={savedRoutes}
              deleteSavedRoute={deleteSavedRoute}
              loadSavedRoute={loadSavedRoute}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Route Details Table */
        .route-details-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .route-details-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .route-details-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .route-details-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .route-details-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .route-details-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .route-details-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .route-details-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .route-details-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default DrivingTimeCalculatorPage;